import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow,
  Devices,
  CheckCircle,
  Error,
  Schedule,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import StatisticsCharts from '../components/StatisticsCharts';
import { API_ENDPOINTS } from '../config/api';

interface Device {
  id: string;
  name: string;
  ip: string;
  status: 'online' | 'offline';
  currentTest?: {
    id: string;
    name: string;
    status: 'running' | 'completed' | 'failed';
    progress: number;
  };
}

interface DashboardStats {
  totalDevices: number;
  onlineDevices: number;
  runningTests: number;
  completedTestsToday: number;
  failedTestsToday: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 获取设备状态
  const { data: devices, isLoading: devicesLoading } = useQuery<Device[]>({
    queryKey: ['devices'],
    queryFn: () => axios.get(API_ENDPOINTS.DEVICES).then(res => res.data),
    refetchInterval: 5000, // 每5秒刷新一次
  });

  // 获取统计数据
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => axios.get(`${API_ENDPOINTS.DASHBOARD}/stats`).then(res => res.data),
    refetchInterval: 30000, // 每30秒刷新一次
  });


  // 获取完整统计信息用于图表
  const { data: fullStats, isLoading: fullStatsLoading } = useQuery({
    queryKey: ['statistics-full'],
    queryFn: () => axios.get(`${API_ENDPOINTS.STATISTICS}/full`).then(res => res.data),
    refetchInterval: 60000, // 每分钟刷新一次
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'error';
      case 'running': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle color="success" />;
      case 'offline': return <Error color="error" />;
      case 'running': return <Schedule color="primary" />;
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      default: return <CheckCircle color="inherit" />;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('dashboard.title')}
      </Typography>

      {/* 统计卡片 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color='textSecondary' gutterBottom>
                    {t('dashboard.totalDevices')}
                  </Typography>
                  <Typography variant="h4">
                    {statsLoading ? '...' : stats?.totalDevices || 0}
                  </Typography>
                </Box>
                <Devices color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color='textSecondary' gutterBottom>
                    {t('dashboard.onlineDevices')}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {statsLoading ? '...' : stats?.onlineDevices || 0}
                  </Typography>
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color='textSecondary' gutterBottom>
                    {t('dashboard.runningTests')}
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {statsLoading ? '...' : stats?.runningTests || 0}
                  </Typography>
                </Box>
                <Schedule color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color='textSecondary' gutterBottom>
                    {t('dashboard.completedToday')}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {statsLoading ? '...' : stats?.completedTestsToday || 0}
                  </Typography>
                </Box>
                <TrendingUp color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 快速操作 */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            {t('dashboard.quickActions')}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              onClick={() => navigate('/test-runner')}
            >
              {t('dashboard.executeFullTest')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/test-runner')}
            >
              {t('dashboard.executePaymentTest')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/test-runner')}
            >
              {t('dashboard.executeLoginTest')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 设备状态 */}
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            {t('dashboard.deviceStatusTitle')}
          </Typography>

          {devicesLoading ? (
            <Typography>{t('dashboard.loading')}</Typography>
          ) : (
            <Grid container spacing={2}>
              {devices?.map((device) => (
                <Grid item xs={12} md={6} lg={4} key={device.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="h6">{device.name}</Typography>
                        <Chip
                          label={device.status}
                          color={getStatusColor(device.status)}
                          size="small"
                          icon={getStatusIcon(device.status)}
                        />
                      </Box>

                      <Typography variant='body2' color='textSecondary' gutterBottom>
                        {t('dashboard.ip')}: {device.ip}
                      </Typography>

                      {device.currentTest && (
                        <Box mt={2}>
                          <Typography variant='body2' gutterBottom>
                            {t('dashboard.currentTest')}: {device.currentTest.name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Chip
                              label={device.currentTest.status}
                              color={getStatusColor(device.currentTest.status)}
                              size="small"
                            />
                            <Typography variant="body2">
                              {device.currentTest.progress}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={device.currentTest.progress}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      )}

                      <Box mt={2}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/devices/${device.id}`)}
                        >
                          {t('dashboard.details')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>


      {/* 详细统计图表 */}
      {fullStats && (
        <Box mt={4}>
          <Typography variant="h5" gutterBottom>
            📈 {t('dashboard.statistics')}
          </Typography>
          <StatisticsCharts data={fullStats} loading={fullStatsLoading} />
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
